import { useState } from "react"
import { CandidateTest, ReviewStatus } from "~/__generated__/graphql"
import { Button } from "~/shadcn/ui/button"
import { useSafeMutation } from "~/common/useSafeMutation"
import toast from "react-hot-toast"
import { gql, useQuery } from "@apollo/client"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/shadcn/ui/dropdown-menu"
import { ChevronDown } from "lucide-react"

export const ReviewStatusButton = ({
  organizationId,
  candidateTest,
}: {
  organizationId: string
  candidateTest: Pick<CandidateTest, "id" | "reviewStatus">
  showIcon?: boolean
}) => {
  const [reviewStatuses, setReviewStatuses] = useState<ReviewStatus[]>([])
  useQuery(REVIEW_STATUSES_QUERY_DOCUMENT, {
    variables: {
      organizationId: organizationId,
    },
    onCompleted: (data) => {
      setReviewStatuses(data.reviewStatuses?.nodes || [])
      return data
    },
  })

  const [setReviewStatus] = useSafeMutation(CANDIDATE_TEST_SET_REVIEW_STATUS)

  const handleReviewStatusChange = async (reviewStatusId: string) => {
    const { data, errors } = await setReviewStatus({
      variables: {
        input: {
          candidateTestId: candidateTest.id,
          reviewStatusId: reviewStatusId,
        },
      },
    })

    if (!data?.candidateTestSetReviewStatus?.candidateTest) {
      toast.error("Error setting review state")
      if (errors) {
        console.log(errors)
      }
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <div className="w-44 text-left">
              {candidateTest.reviewStatus?.name}
            </div>
            <ChevronDown size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {Object.entries(reviewStatuses).map(
            ([_, reviewStatus]: [string, ReviewStatus]) => (
              <DropdownMenuItem
                key={reviewStatus.id}
                onClick={() => {
                  handleReviewStatusChange(reviewStatus.id)
                }}
              >
                {reviewStatus.name}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

const REVIEW_STATUSES_QUERY_DOCUMENT = gql(/* GraphQL */ `
  query reviewStatuses($organizationId: ID!) {
    reviewStatuses(organizationId: $organizationId) {
      nodes {
        id
        name
        position
      }
    }
  }
`)

const CANDIDATE_TEST_SET_REVIEW_STATUS = gql(`
  mutation CandidateTestSetReviewStatus($input: CandidateTestSetReviewStatusInput!) {
    candidateTestSetReviewStatus(input: $input) {
      candidateTest {
        id
        reviewStatus {
          id
          name
        }
      }
    }
  }
`)
